import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import SearchSelect from '../../components/SearchSelect';
import ValidationErrors from '../validationErrors';
import { ValidatorRule } from '../../validator/validatorRule.constants';
import { CountryFieldValidator } from '../../validator/countryFieldValidator';
import { ICountry, ICountryMap } from './country';

export interface ICountryFieldOwnProps {
    countryCodeFieldId?: string;
    countryCodeFieldValue?: string;
    countryNameFieldId?: string;
    countryNameFieldValue?: string;
    autoFocus?: boolean;
    required?: boolean;
    validatorRules?: ValidatorRule[];
    onChange?: (setFieldValue: (field: string, value: any) => void, countryCode: string) => void;
}

export interface ICountryFieldInjectedProps {
    intl: InjectedIntl;
}

export interface ICountryFieldStateProps {
    countriesMap: ICountryMap;
}

type CountryFieldProps = ICountryFieldOwnProps &
    ICountryFieldInjectedProps &
    ICountryFieldStateProps;

interface ICountryFieldState {
    selectedCountry?: ICountry;
    countries: ICountry[];
}

class CountryField extends React.Component<CountryFieldProps, ICountryFieldState> {
    private readonly ID: string;
    private readonly LABEL_ID: string;

    private countryValidator: CountryFieldValidator;

    constructor(props: CountryFieldProps) {
        super(props);

        const { countryNameFieldId, validatorRules } = this.props;

        this.ID = countryNameFieldId ? countryNameFieldId : 'country';
        this.LABEL_ID = 'label.country';

        this.state = CountryField.getCountriesState(props);

        this.validate = this.validate.bind(this);
        this.renderInputField = this.renderInputField.bind(this);
        this.setFieldValues = this.setFieldValues.bind(this);

        this.countryValidator = new CountryFieldValidator(validatorRules);
    }

    public static getDerivedStateFromProps(nextProps: Readonly<CountryFieldProps>) {
        return CountryField.getCountriesState(nextProps);
    }

    public static getCountriesState(nextProps: Readonly<CountryFieldProps>) {
        const { countriesMap, countryCodeFieldValue, countryNameFieldValue } = nextProps;

        if (countriesMap) {
            const countries = Object.values(countriesMap);
            return {
                countries,
                selectedCountry: countryCodeFieldValue
                    ? countriesMap[countryCodeFieldValue] // if country code is known, look up by code
                    : countries.find((country) => country.name === countryNameFieldValue), // otherwise look up by country name
            };
        }

        return {
            countries: [],
        };
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderInputField} />;
    }

    private validate(value: string): string {
        const codeError = this.validateCode();
        return codeError ? codeError : this.validateName();
    }

    private validateCode(): string {
        const { intl, countryCodeFieldId, countriesMap, required } = this.props;
        const { selectedCountry } = this.state;

        if (!countryCodeFieldId) {
            return '';
        }

        if ((!selectedCountry || !selectedCountry.alpha2Code) && required) {
            return ValidationErrors.fieldRequiredMessage(intl, this.LABEL_ID);
        }

        if (selectedCountry) {
            if (selectedCountry.alpha2Code.length !== 2) {
                return ValidationErrors.fieldInvalidMessage(intl, this.LABEL_ID);
            }

            const knownAlpha2Code = countriesMap[selectedCountry.alpha2Code.toUpperCase()];

            if (!knownAlpha2Code) {
                return ValidationErrors.fieldInvalidMessage(intl, this.LABEL_ID);
            }
        }

        return '';
    }

    private validateName(): string {
        const {
            intl,
            countryNameFieldId,
            required,
            validatorRules = [ValidatorRule.CountryLengthRule],
        } = this.props;
        const { selectedCountry } = this.state;

        let errorMessage = '';

        if (!countryNameFieldId) {
            return '';
        }

        if ((!selectedCountry || !selectedCountry.name) && required) {
            return ValidationErrors.fieldRequiredMessage(intl, this.LABEL_ID);
        }

        if (selectedCountry && validatorRules.length > 0) {
            const err = this.countryValidator.validate(selectedCountry.name, this.LABEL_ID);
            if (err !== null) {
                errorMessage = err.message as string;
            }
        }

        return errorMessage;
    }

    private renderInputField({
        form: { touched, errors, setFieldValue, setFieldTouched, validateField },
    }: FieldProps): ReactNode {
        const { required, onChange } = this.props;
        const { selectedCountry, countries } = this.state;
        const isTouched = touched && touched[this.ID];
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;
        const onCountrySelect = (country: ICountry) => {
            this.setState({ selectedCountry: country }, () => {
                this.setFieldValues(
                    setFieldValue,
                    country ? country.alpha2Code : '',
                    country ? country.name : '',
                );
                onChange && onChange(setFieldValue, country.alpha2Code);
                validateField(this.ID);
            });
        };

        return (
            <SearchSelect<ICountry, keyof ICountry>
                autoComplete="country"
                autoCompleteSearchKeys={['name', 'alpha2Code']}
                id={this.ID}
                selectedItem={selectedCountry}
                items={countries}
                label={TranslationHelpers.getMessageDescriptor(this.LABEL_ID)}
                error={errorMessage}
                searchKey={'name'}
                required={required}
                onSelect={onCountrySelect}
                setTouched={setFieldTouched}
                qaId={'country'}
                selectedItemKey={'alpha2Code'}
            />
        );
    }

    private setFieldValues(
        setFieldValue: (key: string, value: string) => void,
        countryCodeFieldValue: string,
        countryNameFieldValue: string,
    ) {
        const { countryCodeFieldId, countryNameFieldId } = this.props;
        if (countryCodeFieldId) {
            setFieldValue(countryCodeFieldId, countryCodeFieldValue);
        }
        if (countryNameFieldId) {
            setFieldValue(countryNameFieldId, countryNameFieldValue);
        }
    }
}

export default injectIntl(CountryField);
